import {
  calculateDTRAdjustment,
  convertGbpToLc,
  covertToPercentage,
} from "../../pages/entity-input/util/DoubleTaxReliefCalc";
import { generateAdjCapId, generateCAPId } from "../../utils/capProcessor";
import { processDateJS } from "../../utils/dateProcessor";
import { getPoaIndex } from "../GroupContext";
/**
 * Please be careful when editing metric_id or display_name. In some parts of the code, these are hard coded values
 * so please ensure you've updated those parts of the code as well.
 */

const displayNames = {
  dn1: "Total per Computation Schedule for period",
  dn2: "      Exclude Exchange gains/(losses)",
  dn3: "      Exclude Impairment gains/(losses)",
  dn4: "      Exclude Tax-Interest Income subject to Double Tax Relief - s388",
  dn5: "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
  dn6: "Adjustment for election for amortised cost basis for fair valued creditor relationships",
  dn7: "Adjustment for election for Qualifying Infrastructure Company",
  dn8: "Exclude defined benefit pension income/expense included on loan relationships statement",
  dn9: "Add bank charges and similar interest type expenses not included in the total above (i.e. not in Loan Relationships schedule)",
  dn10: "Adjustment for GAAP transition items",
  dn11: "Adjustment for finance costs associated with leases",
  dn12: "Adjustment to include Expense (income) associated with finance leases",
  dn13: "Adjustment to include Expense (income) associated with debt factoring transactions",
  dn14: "Adjustment to include Expense (income) associated with service concession arrangement if and to the extent accounted for as a financial liability",
  dn15: "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
  dn16: "Exclude amounts on contracts arising in the ordinary course of trade",
  dn17: "Exclude amounts for derivatives without an underlying subject matter specified in s384(4) / s387(4)",
  dn18: "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
  dn19: "Other Adjustments 1",
  dn20: "Other Adjustments 2",
  dn21: "Other Adjustments 3",
};

const getMetrics = (tooltips = {}) => {
  return [
    {
      metric_id: "start_date",
      display_name: "Accounting Period Start Date",
      mandatory: true,
      type: "date",
      editable: false,
    },
    {
      metric_id: "end_date",
      display_name: "Accounting Period End Date",
      mandatory: true,
      type: "date",
      editable: false,
    },
    {
      metric_id: "currency",
      display_name: "Computation Currency",
      mandatory: true,
      type: "text",
    },
    {
      metric_id: "fx_rate",
      display_name: "Exchange Rate (GBP1:CURX)",
      mandatory: true,
      type: "number",
    },
    {
      metric_id: "onesource_sync",
      display_name: "Enable OneSource data extraction",
      mandatory: false,
      type: "boolean",
      read_only: (cap) => {
        return !cap.from_onesource;
      },
    },
    {
      metric_id: "updated_date_db",
      display_name: "OneSource last updated",
      mandatory: false,
      editable: false,
    },
    {
      metric_id: "onesource_last_refresh_date_time",
      display_name: "App last refreshed from OneSource",
      mandatory: false,
      editable: false,
    },
    { type: "blank" },

    {
      type: "header",
      display_name: "Net Tax-Interest Expense/(Income) Section",
    },
    {
      type: "subheader",
      display_name: "Non-trading loan relationships",
      style: "bold",
    },
    {
      metric_id: "nltr_total_per_source_sheet_for_period",
      display_name: displayNames?.dn1,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      onesourcedredged: true,
      tooltipMessage: tooltips.nltr_total_per_source_sheet_for_period,
    },
    { type: "note", display_name: "Less: included in the above" },
    {
      metric_id: "nltr_exclude_exchange_gains_losses",
      display_name: displayNames?.dn2,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_exclude_exchange_gains_losses,
    },
    {
      metric_id: "nltr_exclude_impairment_gains_losses",
      display_name: displayNames?.dn3,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_exclude_impairment_gains_losses,
    },
    {
      metric_id: "nltr_exclude_tax_interest_income_double_relief",
      display_name: displayNames?.dn4,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.tlr_exclude_tax_interest_income_double_relief,
    },
    {
      metric_id: "nltr_adjustment_amortised_cost_basis",
      display_name: displayNames?.dn6,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_adjustment_amortised_cost_basis,
    },
    {
      metric_id: "nltr_adjustment_qualifying_election",
      display_name: displayNames?.dn7,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_adjustment_qualifying_election,
    },
    {
      metric_id: "nltr_exclude_defined_benefit_pension",
      display_name: displayNames?.dn8,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_exclude_defined_benefit_pension,
    },
    {
      metric_id: "nltr_bank_expenses",
      display_name: displayNames?.dn9,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_bank_expenses,
    },
    {
      metric_id: "nltr_adjustment_gaap_transition",
      display_name: displayNames?.dn10,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_adjustment_gaap_transition,
    },
    {
      metric_id: "nltr_adjustment_lease_finance_cost",
      display_name: displayNames?.dn11,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_adjustment_lease_finance_cost,
    },
    {
      metric_id: "nltr_other_adjustments_1",
      display_name: displayNames?.dn19,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_other_adjustments_1,
    },
    {
      metric_id: "nltr_other_adjustments_2",
      display_name: displayNames?.dn20,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_other_adjustments_2,
    },
    {
      metric_id: "nltr_other_adjustments_3",
      display_name: displayNames?.dn21,
      mandatory: true,
      type: "number",
      calculation_group: ["ntlr"],
      tooltipMessage: tooltips.nltr_other_adjustments_3,
    },
    {
      metric_id: "ntlr_subtotal",
      display_name: "Non-trading loan relationships - Subtotal",
      type: "calculation",
      style: "bold",
      calculation: (company) => {
        return sumCalculationGroup(company, "ntlr");
      },
    },

    { type: "blank" },

    {
      type: "subheader",
      display_name: "Non-trading derivative contracts",
      style: "bold",
    },
    {
      metric_id: "ntdc_total_per_source_sheet_for_period",
      display_name: displayNames?.dn1,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      onesourcedredged: true,
      tooltipMessage: tooltips.ntdc_total_per_source_sheet_for_period,
    },
    { type: "note", display_name: "Less: included in the above" },
    {
      metric_id: "ntdc_exclude_exchange_gains_losses",
      display_name: displayNames?.dn2,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_exclude_exchange_gains_losses,
    },
    {
      metric_id: "ntdc_exclude_impairment_gains_losses",
      display_name: displayNames?.dn3,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_exclude_impairment_gains_losses,
    },
    {
      metric_id: "ntdc_exclude_tax_interest_income_double_relief",
      display_name: displayNames?.dn4,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_exclude_tax_interest_income_double_relief,
    },
    {
      metric_id: "ntdc_exclude_ordinary_trade",
      display_name: displayNames?.dn16,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_exclude_ordinary_trade,
    },
    {
      metric_id:
        "ntdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)",
      display_name: displayNames?.dn17,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage:
        tooltips.ntdc_exclude_derivatives_without_subject_matters_384_4_s387_4,
    },
    {
      metric_id: "ntdc_adjustment_qualifying_election",
      display_name: displayNames?.dn7,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_adjustment_qualifying_election,
    },
    {
      metric_id: "ntdc_adjustment_gaap_transition",
      display_name: displayNames?.dn10,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_adjustment_gaap_transition,
    },
    {
      metric_id: "ntdc_adjustment_disregard_regulations",
      display_name: displayNames?.dn18,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_adjustment_disregard_regulations,
    },
    {
      metric_id: "ntdc_other_adjustments_1",
      display_name: displayNames?.dn19,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_other_adjustments_1,
    },
    {
      metric_id: "ntdc_other_adjustments_2",
      display_name: displayNames?.dn20,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_other_adjustments_2,
    },
    {
      metric_id: "ntdc_other_adjustments_3",
      display_name: displayNames?.dn21,
      mandatory: true,
      type: "number",
      calculation_group: ["ntdc"],
      tooltipMessage: tooltips.ntdc_other_adjustments_3,
    },
    {
      metric_id: "ntdc_subtotal",
      display_name: "Non-trading derivative contracts - Subtotal",
      type: "calculation",
      style: "bold",
      calculation: (company) => {
        return sumCalculationGroup(company, "ntdc");
      },
    },

    { type: "blank" },

    {
      type: "subheader",
      display_name: "Trading loan relationships",
      style: "bold",
    },
    {
      metric_id: "tlr_total_per_source_sheet_for_period",
      display_name: displayNames?.dn1,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      onesourcedredged: true,
      tooltipMessage: tooltips.tlr_total_per_source_sheet_for_period,
    },
    { type: "note", display_name: "Less: included in the above" },
    {
      metric_id: "tlr_exclude_exchange_gains_losses",
      display_name: displayNames?.dn2,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_exclude_exchange_gains_losses,
    },
    {
      metric_id: "tlr_exclude_impairment_gains_losses",
      display_name: displayNames?.dn3,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_exclude_impairment_gains_losses,
    },
    {
      metric_id: "tlr_exclude_tax_interest_income_double_relief",
      display_name: displayNames?.dn4,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_exclude_tax_interest_income_double_relief,
    },
    {
      metric_id: "tlr_adjustment_amortised_cost_basis",
      display_name: displayNames?.dn6,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_adjustment_amortised_cost_basis,
    },
    {
      metric_id: "tlr_adjustment_qualifying_election",
      display_name: displayNames?.dn7,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_adjustment_qualifying_election,
    },
    {
      metric_id: "tlr_exclude_defined_benefit_pension",
      display_name: displayNames?.dn8,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_exclude_defined_benefit_pension,
    },
    {
      metric_id: "tlr_bank_expenses",
      display_name: displayNames?.dn9,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_bank_expenses,
    },
    {
      metric_id: "tlr_adjustment_gaap_transition",
      display_name: displayNames?.dn10,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_adjustment_gaap_transition,
    },
    {
      metric_id: "tlr_adjustment_lease_finance_cost",
      display_name: displayNames?.dn11,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_adjustment_lease_finance_cost,
    },
    {
      metric_id: "tlr_other_adjustments_1",
      display_name: displayNames?.dn19,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_other_adjustments_1,
    },
    {
      metric_id: "tlr_other_adjustments_2",
      display_name: displayNames?.dn20,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_other_adjustments_2,
    },
    {
      metric_id: "tlr_other_adjustments_3",
      display_name: displayNames?.dn21,
      mandatory: true,
      type: "number",
      calculation_group: ["tlr"],
      tooltipMessage: tooltips.tlr_other_adjustments_3,
    },
    {
      metric_id: "tlr_subtotal",
      display_name: "Trading loan relationships - Subtotal",
      type: "calculation",
      style: "bold",
      calculation: (company) => {
        return sumCalculationGroup(company, "tlr");
      },
    },

    { type: "blank" },

    {
      type: "subheader",
      display_name: "Trading derivative contracts",
      style: "bold",
    },
    {
      metric_id: "tdc_total_per_source_sheet_for_period",
      display_name: displayNames?.dn1,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      onesourcedredged: true,
      tooltipMessage: tooltips.tdc_total_per_source_sheet_for_period,
    },
    { type: "note", display_name: "Less: included in the above" },
    {
      metric_id: "tdc_exclude_exchange_gains_losses",
      display_name: displayNames?.dn2,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.tdc_exclude_exchange_gains_losses,
    },
    {
      metric_id: "tdc_exclude_impairment_gains_losses",
      display_name: displayNames?.dn3,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.tdc_exclude_impairment_gains_losses,
    },
    {
      metric_id: "tdc_exclude_tax_interest_income_double_relief",
      display_name: displayNames?.dn4,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.tdc_exclude_tax_interest_income_double_relief,
    },
    {
      metric_id: "tdc_exclude_ordinary_trade",
      display_name: displayNames?.dn16,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.ntdc_exclude_ordinary_trade,
    },
    {
      metric_id:
        "tdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)",
      display_name: displayNames?.dn17,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage:
        tooltips.ntdc_exclude_derivatives_without_subject_matters_384_4_s387_4,
    },
    {
      metric_id: "tdc_adjustment_qualifying_election",
      display_name: displayNames?.dn7,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.tdc_adjustment_qualifying_election,
    },
    {
      metric_id: "tdc_adjustment_gaap_transition",
      display_name: displayNames?.dn10,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.tdc_adjustment_gaap_transition,
    },
    {
      metric_id: "tdc_adjustment_disregard_regulations",
      display_name: displayNames?.dn18,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.tdc_adjustment_disregard_regulations,
    },
    {
      metric_id: "tdc_other_adjustments_1",
      display_name: displayNames?.dn19,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.tdc_other_adjustments_1,
    },
    {
      metric_id: "tdc_other_adjustments_2",
      display_name: displayNames?.dn20,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.tdc_other_adjustments_2,
    },
    {
      metric_id: "tdc_other_adjustments_3",
      display_name: displayNames?.dn21,
      mandatory: true,
      type: "number",
      calculation_group: ["tdc"],
      tooltipMessage: tooltips.tdc_other_adjustments_3,
    },
    {
      metric_id: "tdc_subtotal",
      display_name: "Trading derivative contracts - Subtotal",
      type: "calculation",
      style: "bold",
      calculation: (company) => {
        return sumCalculationGroup(company, "tdc");
      },
    },

    { type: "blank" },

    {
      type: "subheader",
      display_name: "Other financing arrangements",
      style: "bold",
    },
    {
      metric_id: "ofa_total_per_source_sheet_for_period",
      display_name: displayNames?.dn1,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      onesourcedredged: true,
      tooltipMessage: tooltips.ofa_total_per_source_sheet_for_period,
    },
    { type: "note", display_name: "Less: included in the above" },
    {
      metric_id: "ofa_exclude_tax_interest_income_double_relief",
      display_name: displayNames?.dn4,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      tooltipMessage: tooltips.ofa_exclude_tax_interest_income_double_relief,
    },
    {
      metric_id: "ofa_adjustment_qualifying_election",
      display_name: displayNames?.dn7,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      tooltipMessage: tooltips.ofa_adjustment_qualifying_election,
    },
    {
      metric_id: "ofa_adjustment_gaap_transition",
      display_name: displayNames?.dn10,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      tooltipMessage: tooltips.ofa_adjustment_gaap_transition,
    },
    {
      metric_id: "ofa_adjustment_finance_lease",
      display_name: displayNames?.dn12,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      tooltipMessage: tooltips.ofa_adjustment_finance_lease,
    },
    {
      metric_id: "ofa_adjustment_debt_factoring",
      display_name: displayNames?.dn13,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      tooltipMessage: tooltips.ofa_adjustment_debt_factoring,
    },
    {
      metric_id: "ofa_adjustment_service_concession",
      display_name: displayNames?.dn14,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      tooltipMessage: tooltips.ofa_adjustment_service_concession,
    },
    {
      metric_id: "ofa_other_adjustments_1",
      display_name: displayNames?.dn19,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      tooltipMessage: tooltips.ofa_other_adjustments_1,
    },
    {
      metric_id: "ofa_other_adjustments_2",
      display_name: displayNames?.dn20,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      tooltipMessage: tooltips.ofa_other_adjustments_2,
    },
    {
      metric_id: "ofa_other_adjustments_3",
      display_name: displayNames?.dn21,
      mandatory: true,
      type: "number",
      calculation_group: ["ofa"],
      tooltipMessage: tooltips.ofa_other_adjustments_3,
    },
    {
      metric_id: "ofa_subtotal",
      display_name: "Other financing arrangements - Subtotal",
      type: "calculation",
      style: "bold",
      calculation: (company) => {
        return sumCalculationGroup(company, "ofa");
      },
    },

    { type: "blank" },
    {
      metric_id: "net_tax_interest_totals",
      style: "bigBold",
      display_name: "Total net Tax-Interest Expense / (Income)",
      type: "calculation",
      calculation: (company) => {
        return calculateNetTaxInterestExpense(company);
      },
    },
    { type: "blank" },

    { type: "header", display_name: "Tax-EBITDA section" },
    {
      metric_id: "cte_condition_a",
      display_name:
        "Adjusted corporation tax earnings - Condition A (TIOPA 2010, s406(3))",
      mandatory: true,
      type: "number",
      calculation_group: ["cte_subtotal", "tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.cte_condition_a,
    },
    {
      metric_id: "cte_condition_b",
      display_name:
        "Adjusted corporation tax earnings - Condition B (TIOPA 2010, s406(4))",
      mandatory: true,
      type: "number",
      calculation_group: ["cte_subtotal", "tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.cte_condition_b,
    },
    {
      metric_id: "cte_restrictions_reactivations_included",
      display_name:
        "CIR (Restrictions) and reactivations included in the above",
      mandatory: true,
      type: "number",
      calculation_group: ["cte_subtotal", "tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.cte_restrictions_reactivations_included,
    },
    {
      metric_id: "tebitda_adjustments",
      display_name: "Adjustments",
      mandatory: true,
      type: "number",
      calculation_group: ["cte_subtotal", "tax_ebitda"],
      tooltipMessage: tooltips.tebitda_adjustments,
    },
    {
      metric_id: "cte_subtotal",
      style: "bold",
      display_name:
        "Corporation tax earnings (excluding restrictions and reactivations)",
      type: "calculation",
      calculation: (company) => {
        return sumCalculationGroup(company, "cte_subtotal");
      },
    },

    { type: "blank" },

    {
      type: "subheader",
      display_name: "Adjustments - s407 TIOPA 2010",
      style: "bold",
    },
    {
      metric_id: "s407_tiopa_net_tax_interest_expense",
      display_name: "Net tax-interest expense/(income) - s407(1)(a)",
      editable: false,
      type: "calculation",
      calculation: (company) => {
        return calculateNetTaxInterestExpense(company);
      },
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_tiopa_net_tax_interest_expense,
    },
    {
      metric_id: "s407_tiopa_capital_allowance_charges",
      display_name: "Capital allowances and charges - s407(1)(b)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_tiopa_capital_allowance_charges,
    },
    {
      metric_id: "s407_tiopa_relevant_intangible_debits_credits",
      display_name: "Relevant intangible debits and credits - s407(1)(c)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_tiopa_relevant_intangible_debits_credits,
    },
    {
      metric_id: "s407_tiopa_adjustment_intangible_debit_credits",
      display_name:
        "Adjustment to intangible debits and credits brought in automatically (e.g. disposals above original cost)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_tiopa_adjustment_intangible_debit_credits,
    },

    {
      metric_id: "s407_tiopa_other_period_losses",
      display_name: "Losses from other periods - s407(1)(d)(i) and (ii)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_tiopa_other_period_losses,
    },
    {
      metric_id: "s407_tiopa_loan_relationship_deficts_other_periods",
      display_name:
        "Loan relationship deficits from other periods - s407(1)(e)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage:
        tooltips.s407_tiopa_loan_relationship_deficts_other_periods,
    },
    {
      metric_id: "s407_tiopa_expenses_management_other_periods",
      display_name: "Expenses of management from other periods - s407(1)(f)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_tiopa_expenses_management_other_periods,
    },
    {
      metric_id: "s407_tiopa_group_relief",
      display_name: "Group relief - s407(1)(g)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_tiopa_group_relief,
    },
    {
      metric_id: "s407_relief_group_relief",
      display_name: "Group relief included above not covered by s407(1)(g)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_relief_group_relief,
    },
    {
      metric_id: "s407_relief_rd_expenditure_credit",
      display_name: "R&D expenditure credit - s407(3)(a)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_rd_expenditure_credit,
    },
    {
      metric_id: "s407_relief_rd_additional_relief",
      display_name: "R&D additional relief - s407(3)(b)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_rd_additional_relief,
    },
    {
      metric_id: "s407_relief_trading_loss",
      display_name:
        "Deemed trading loss for pre-trading expenditure - s407(3)(c)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_trading_loss,
    },
    {
      metric_id: "s407_relief_contaminated_land_relief",
      display_name: "Contaminated land relief - s407(3)(d)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_contaminated_land_relief,
    },
    {
      metric_id: "s407_relief_creative_industries",
      display_name: "Creative industries - s407(3)(e)-(j)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_creative_industries,
    },
    {
      metric_id: "s407_relief_charitable_donation",
      display_name:
        "Qualifying charitable donation (from current or earlier period) - s407(3)(k)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_charitable_donation,
    },
    {
      metric_id: "s407_relief_patent_box_profits",
      display_name:
        "Profits from patent box chargeable at a lower rate - s407(3)(l)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_patent_box_profits,
    },
    {
      metric_id: "s407_relief_dtr_adjustment_tax_rate_equal_uk",
      display_name: "DTR adjustment - s409",
      mandatory: true,
      type: "calculation",
      calculation_group: ["tax_ebitda"],
      editable: false,
      calculation: (company) => {
        company.net_dtr_adjustment_lc = convertGbpToLc(
          company.fx_rate,
          company.net_dtr_adjustment_gbp,
        );
        return calculateDTRAdjustment(
          company.net_dtr_adjustment_lc,
          company.corporation_tax_rate,
          company.ct_rate_override,
        );
      },
      tooltipMessage: tooltips.s407_relief_dtr_adjustment_tax_rate_equal_uk,
    },
    {
      metric_id: "s407_relief_adjustment_qualifying_infrastructure",
      display_name: displayNames?.dn7,
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_relief_adjustment_qualifying_infrastructure,
    },
    {
      metric_id: "s407_relief_adjustment_gaap_items",
      display_name: displayNames?.dn10,
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_relief_adjustment_gaap_items,
    },
    {
      metric_id: "s407_relief_finance_lease_depreciation",
      display_name: "Finance lease depreciation - s460(1)(d)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_finance_lease_depreciation,
    },
    {
      metric_id: "s407_relief_reversal_of_lease_depreciation",
      display_name:
        "Reversal of right-of-use lease depreciation in respect of operating leases",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_relief_reversal_of_lease_depreciation,
    },
    {
      metric_id: "s407_relief_long_funding_lease_deduction",
      display_name: "Long funding lease deduction - s460(1)(a)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_long_funding_lease_deduction,
    },
    {
      metric_id: "s407_relief_finance_lease_rentals_receivable",
      display_name:
        "Capital element of finance lease rentals receivable - s460(1)(c)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_finance_lease_rentals_receivable,
    },
    {
      metric_id: "s407_relief_long_funding_lessee",
      display_name:
        "Long funding lessee - amount by which a deduction is reduced under section 379 of CTA 2010 - s460(1)(b)",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      onesourcedredged: true,
      tooltipMessage: tooltips.s407_relief_long_funding_lessee,
    },
    {
      metric_id: "s407_relief_adjustment_misc_losses",
      display_name:
        "Adjustment for miscellaneous losses not included in Condition B above",
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_relief_adjustment_misc_losses,
    },
    {
      metric_id: "s407_relief_adjustment_disregarded_regulations",
      display_name: displayNames?.dn15,
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_relief_adjustment_disregarded_regulations,
    },
    {
      metric_id: "s407_relief_other_adjustments_1",
      display_name: displayNames?.dn19,
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_relief_other_adjustments_1,
    },
    {
      metric_id: "s407_relief_other_adjustments_2",
      display_name: displayNames?.dn20,
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_relief_other_adjustments_2,
    },
    {
      metric_id: "s407_relief_other_adjustments_3",
      display_name: displayNames?.dn21,
      mandatory: true,
      type: "number",
      calculation_group: ["tax_ebitda"],
      tooltipMessage: tooltips.s407_relief_other_adjustments_3,
    },
    {
      metric_id: "tax_ebitda",
      display_name: "Tax-EBITDA - Subtotal",
      type: "calculation",
      style: "bigBold",
      calculation: (company) => {
        return calculateTaxEBITDA(company);
      },
    },

    { type: "blank" },

    // Double tax relief adjustment
    { type: "header", display_name: "DTR adjustment s409 section" },
    {
      metric_id: "net_dtr_adjustment_gbp",
      display_name: "Credit for foreign taxes under s18(2) TIOPA 2010 in GBP",
      mandatory: true,
      type: "number",
      calculation_group: "dtr_values",
      onesourcedredged: true,
      tooltipMessage: tooltips.net_dtr_adjustment_gbp,
    },
    {
      metric_id: "net_dtr_adjustment_lc",
      display_name:
        "Credit for foreign taxes under s18(2) TIOPA 2010 in Local Currency",
      mandatory: true,
      type: "calculation",
      calculation_group: "dtr_values",
      calculation: (company) => {
        company.net_dtr_adjustment_lc = convertGbpToLc(
          company.fx_rate,
          company.net_dtr_adjustment_gbp,
        );
        return company.net_dtr_adjustment_lc;
      },
      tooltipMessage: tooltips.net_dtr_adjustment_lc,
    },
    {
      metric_id: "corporation_tax_rate_percentage",
      display_name: "Corporation Tax Rate",
      mandatory: true,
      type: "calculation",
      calculation_group: "dtr_values",
      calculation: (company) => {
        return covertToPercentage(company.corporation_tax_rate);
      },
      tooltipMessage: tooltips.corporation_tax_rate_percentage,
    },
    {
      metric_id: "ct_rate_override",
      display_name: "CT Rate override",
      mandatory: true,
      type: "number",
      calculation_group: "dtr_values",
      tooltipMessage: tooltips.dtr_values,
    },
    {
      metric_id: "s407_relief_dtr_adjustment_tax_rate_uk",
      display_name: "DTR adjustment - s409",
      mandatory: true,
      type: "calculation",
      style: "bold",
      calculation_group: "dtr_values",
      editable: false,
      calculation: (company) => {
        return calculateDTRAdjustment(
          company.net_dtr_adjustment_lc,
          company.corporation_tax_rate,
          company.ct_rate_override,
        );
      },
      tooltipMessage: tooltips.s407_relief_dtr_adjustment_tax_rate_uk,
    },

    { type: "blank" },

    {
      type: "subheader",
      display_name: "CIR specific attributes",
      style: "bold",
    },
    {
      metric_id: "non_consenting_company",
      display_name: "Non-consenting company?",
      mandatory: true,
      type: "boolean",
      calculation_group: ["cir_attributes"],
      tooltipMessage: tooltips.non_consenting_company,
    },
    {
      metric_id: "trade_small_or_negligible",
      display_name: "Trade ceased and/or small or negligible?",
      mandatory: true,
      type: "boolean",
      calculation_group: ["cir_attributes"],
      tooltipMessage: tooltips.trade_small_or_negligible,
    },
    {
      metric_id: "trade_uncommercial_or_non_statutory",
      display_name: "Trade uncommercial or non-statutory?",
      mandatory: true,
      type: "boolean",
      calculation_group: ["cir_attributes"],
      tooltipMessage: tooltips.trade_uncommercial_or_non_statutory,
    },
    {
      metric_id: "investment_business_ceased",
      display_name: "Investment business ceased?",
      mandatory: true,
      type: "boolean",
      calculation_group: ["cir_attributes"],
      tooltipMessage: tooltips.investment_business_ceased,
    },
    {
      metric_id: "cta10_change_in_ownership_rules",
      display_name: "CTA10 Change in Company Ownership rules apply?",
      mandatory: true,
      type: "boolean",
      calculation_group: ["cir_attributes"],
      tooltipMessage: tooltips.cta10_change_in_ownership_rules,
    },

    { type: "blank" },

    { type: "subheader", display_name: "Company Type", style: "bold" },
    {
      metric_id: "type_reit_company",
      display_name: "REIT",
      mandatory: true,
      type: "boolean",
      calculation_group: ["company_type"],
      tooltipMessage: tooltips.type_reit_company,
    },
    {
      metric_id: "type_ring_fence_company",
      display_name: "Oil and gas ring fence company",
      mandatory: true,
      type: "boolean",
      calculation_group: ["company_type"],
      tooltipMessage: tooltips.type_ring_fence_company,
    },
    {
      metric_id: "type_investment_manager",
      display_name: "Investment manager",
      mandatory: true,
      type: "boolean",
      calculation_group: ["company_type"],
      tooltipMessage: tooltips.type_investment_manager,
    },
    {
      metric_id: "type_insurance_company",
      display_name: "Insurance company",
      mandatory: true,
      type: "boolean",
      calculation_group: ["company_type"],
      tooltipMessage: tooltips.type_insurance_company,
    },
    {
      metric_id: "type_shipping_company",
      display_name: "Shipping company",
      mandatory: true,
      type: "boolean",
      calculation_group: ["company_type"],
      tooltipMessage: tooltips.type_shipping_company,
    },
    {
      metric_id: "type_cooperative_company",
      display_name: "Coopoerative/community benefit society etc",
      mandatory: true,
      type: "boolean",
      calculation_group: ["company_type"],
      tooltipMessage: tooltips.type_cooperative_company,
    },
    {
      metric_id: "type_charity_company",
      display_name: "Charity",
      mandatory: true,
      type: "boolean",
      calculation_group: ["company_type"],
      tooltipMessage: tooltips.type_charity_company,
    },
    {
      metric_id: "type_banking_company",
      display_name: "Banking company",
      mandatory: true,
      type: "boolean",
      calculation_group: ["company_type"],
      tooltipMessage: tooltips.type_banking_company,
    },

    { type: "blank" },

    { type: "subheader", display_name: "Elections:", style: "bold" },
    // {
    //   metric_id: "apply_mean_accounting_date_election",
    //   display_name: "Apply Mean Accounting Date election",
    //   mandatory: false,
    //   type: "boolean",
    //   calculation_group: ["elections"],
    //   tooltipMessage: ["TBC"],
    //   disabledToolTipMessage: ["Disabled: AP dates match PoA dates."],
    // },
    // {
    //   metric_id: "actual_start_date",
    //   display_name: "Actual Accounting Period start date",
    //   mandatory: false,
    //   type: "date",
    //   calculation_group: ["elections"],
    //   tooltipMessage: ["TBC"],
    // },
    // {
    //   metric_id: "actual_end_date",
    //   display_name: "Actual Accounting Period end date",
    //   mandatory: false,
    //   type: "date",
    //   calculation_group: ["elections"],
    //   tooltipMessage: ["TBC"],
    // },
    {
      metric_id: "elections_s456_s457",
      display_name:
        "S456 & 457 Election for amortised cost basis for fair valued creditor relationships",
      mandatory: true,
      type: "boolean",
      calculation_group: ["elections"],
      tooltipMessage: tooltips.elections_s456_s457,
    },
    {
      metric_id: "elections_amortised_cost_basis",
      display_name:
        "Election for amortised cost basis for fair valued creditor relationships - Date of election",
      mandatory: true,
      type: "date",
      calculation_group: ["elections"],
      tooltipMessage: tooltips.elections_amortised_cost_basis,
    },
    {
      metric_id: "election_s433_s434",
      display_name: "S433 & 434 Qualifying infrastructure election",
      mandatory: true,
      type: "boolean",
      calculation_group: ["elections"],
      tooltipMessage: tooltips.election_s433_s434,
    },
    {
      metric_id: "qualifying_infrastructure_election_date",
      display_name:
        "Qualifying Infrastructure Election - Date of election  company",
      mandatory: true,
      type: "date",
      calculation_group: ["elections"],
      tooltipMessage: tooltips.qualifying_infrastructure_election_date,
    },
    {
      metric_id: "qualifying_infrastructure_first_ap_start",
      display_name:
        "Qualifying Infrastructure Election - First relevant AP start",
      mandatory: true,
      type: "date",
      calculation_group: ["elections"],
      tooltipMessage: tooltips.qualifying_infrastructure_first_ap_start,
    },
  ];
};

const sumCompanyMetrics = (company, metrics) => {
  let total = 0;
  for (const metricId of metrics) {
    const value = company[metricId];
    if (!value) {
      continue;
    }

    total += Math.round(value);
  }

  return total;
};

const sumCalculationGroup = (company, calculationGroup) => {
  const metrics = getMetrics();
  const group_metrics = metrics?.filter((metric) => {
    const group = metric?.calculation_group;
    if (Array.isArray(group)) {
      return group?.includes(calculationGroup);
    } else {
      return group === calculationGroup;
    }
  });

  if (group_metrics?.length === 0) {
    return 0;
  }

  let total = 0;
  for (const metric of group_metrics) {
    let value;
    if (metric?.type === "calculation") {
      value = metric?.calculation(company);
    } else {
      if (typeof company[metric?.metric_id] === "object") {
        value = company[metric?.metric_id]?.value;
      } else {
        value = company[metric?.metric_id] ? company[metric?.metric_id] : 0;
      }
    }
    if (!value) {
      continue;
    }
    total += Math.round(value);
  }
  return total;
};

const calculateNetTaxInterestExpense = (company) => {
  return (
    sumCalculationGroup(company, "ntlr") +
    sumCalculationGroup(company, "ntdc") +
    sumCalculationGroup(company, "tlr") +
    sumCalculationGroup(company, "tdc") +
    sumCalculationGroup(company, "ofa")
  );
};

const calculateTaxEBITDA = (company) => {
  return sumCalculationGroup(company, "tax_ebitda");
};

const bfd_cfd_values = [
  {
    bfd: "bfd_non_trading_loan_relationships",
    cfd: "cfd_non_trading_loan_relationships",
  },
  { bfd: "bfd_non_trading_derivatives", cfd: "cfd_non_trading_derivatives" },
  {
    bfd: "bfd_trading_loan_relationships",
    cfd: "cfd_trading_loan_relationships",
  },
  { bfd: "bfd_trading_derivatives", cfd: "cfd_trading_derivatives" },
  { bfd: "bfd_other_financing", cfd: "cfd_other_financing" },
  { bfd: "bfd_subtotal", cfd: "cfd_subtotal" },
];

// copies bfd value in prior_ap into cfd value in current_ap
const convertBFDCFD = (prior_ap, current_ap) => {
  bfd_cfd_values?.forEach((field) => {
    if (prior_ap?.hasOwnProperty(field?.cfd)) {
      current_ap[field?.bfd] = Math.round(prior_ap[field?.cfd]);
    }
  });
  return current_ap;
};

const getBFDValues = (savedData, poa, ap) => {
  // If this is the first cap for the company, set ap[field.bfd] to its stored value if available; otherwise, use the corresponding company ap bfd value. If neither is available, set it to 0.
  if (ap.isFirstApForCompanyInGroup) {
    const correspondingCompAP = savedData?.company_accounting_periods?.find(
      (compAP) => generateCAPId(compAP) === generateCAPId(ap),
    );
    bfd_cfd_values?.forEach((field) => {
      // Set ap[field.bfd] to its current value if defined; otherwise, use the correspondingCompAP[field.bfd] value. If both are null or undefined, set it to 0.
      ap[field?.bfd] = ap[field?.bfd] ?? correspondingCompAP[field?.bfd] ?? 0;
    });
    return ap;
  }

  // search for prior AP in the same POA
  if (poa?.hasOwnProperty("adjusted_caps") && poa?.adjusted_caps?.length > 0) {
    const prior_ap = poa?.adjusted_caps?.find(
      (cap) =>
        cap?.company_ID === ap?.company_ID &&
        processDateJS(cap?.end_date)?.isSame(
          processDateJS(ap?.start_date)?.subtract(1, "day"),
        ),
    );
    if (prior_ap) {
      // return bfd amounts in current period = cfd amounts in prior period
      ap["priorApCurrency"] = prior_ap.currency;
      return convertBFDCFD(prior_ap, ap);
    }
  }

  // find prior poa and search here
  const prior_poa = savedData?.periods_of_account?.find((prior_poa) =>
    processDateJS(prior_poa?.period_end)?.isSame(
      processDateJS(poa?.period_start)?.subtract(1, "day"),
    ),
  );
  if (
    prior_poa &&
    prior_poa?.hasOwnProperty("adjusted_caps") &&
    prior_poa?.adjusted_caps?.length > 0
  ) {
    const prior_poa_ap = prior_poa?.adjusted_caps?.find(
      (cap) =>
        cap?.company_ID === ap?.company_ID &&
        processDateJS(cap?.inclusion_period_end)?.isSame(
          processDateJS(ap?.inclusion_period_start)?.subtract(1, "day"),
        ),
    );
    if (prior_poa_ap) {
      // return bfd amounts in current period = cfd amounts in prior period
      ap["priorApCurrency"] = prior_poa_ap.currency;
      return convertBFDCFD(prior_poa_ap, ap);
    }
  }

  // return unchanged ap if no condition is satisfied
  return ap;
};

const checkIfFirstAdjCapInGroup = (savedData, adjCap) => {
  const adjCapCompId = adjCap?.company_ID;
  const firstAdjustedCapForCompany = savedData.periods_of_account
    .flatMap((poa) => poa.adjusted_caps)
    .filter((adjustedCap) => adjustedCap?.company_ID === adjCapCompId)
    .reduce((earliest, current) => {
      return processDateJS(current?.inclusion_period_start).isBefore(
        processDateJS(earliest?.inclusion_period_start),
      )
        ? current
        : earliest;
    });
  return (
    generateAdjCapId(adjCap) === generateAdjCapId(firstAdjustedCapForCompany)
  );
};

const apExistsInPriorPoasAdjCaps = (savedData, cap) => {
  const apExists =
    savedData.periods_of_account
      .slice(0, getPoaIndex())
      .flatMap((poa) => poa.adjusted_caps)
      .filter((adjustedCap) => adjustedCap?.company_ID === cap?.company_ID)
      .length > 0;

  return apExists;
};

export {
  apExistsInPriorPoasAdjCaps,
  calculateNetTaxInterestExpense,
  calculateTaxEBITDA,
  checkIfFirstAdjCapInGroup,
  getBFDValues,
  getMetrics,
  sumCalculationGroup,
};
